import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { LOGO_URLS, WHATSAPP_LINK } from "../../lib/constants";
import { Menu, X, ShoppingBag, MessageCircle } from "lucide-react";

const Navbar = ({ activeSection, cartCount, onCartClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { id: "productos", label: "Productos" },
    { id: "arma-tu-caja", label: "Arma tu Caja" },
    { id: "quiz", label: "Quiz" },
    { id: "ruleta", label: "Ruleta" },
    { id: "regala", label: "Regala" },
    { id: "testimonios", label: "Opiniones" },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass shadow-lg" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <motion.a
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("hero");
              }}
              whileHover={{ scale: 1.05 }}
              className="flex items-center"
              data-testid="navbar-logo"
            >
              <img
                src={LOGO_URLS.transparent}
                alt="Urban Brownies"
                className={`h-10 md:h-12 w-auto transition-all duration-300 ${
                  isScrolled ? "" : "brightness-0 invert"
                }`}
              />
            </motion.a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeSection === item.id
                      ? "bg-[#C88037] text-white"
                      : isScrolled
                      ? "text-[#2D1810] hover:bg-[#F4EDE4]"
                      : "text-white/90 hover:text-white hover:bg-white/10"
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2">
              {/* Cart Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onCartClick}
                className={`relative p-3 rounded-full transition-colors ${
                  isScrolled
                    ? "bg-[#F4EDE4] text-[#2D1810] hover:bg-[#E5DCD0]"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
                data-testid="cart-button"
              >
                <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#C88037] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </motion.button>

              {/* WhatsApp Button - Desktop */}
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-full font-semibold transition-colors ${
                  isScrolled
                    ? "bg-[#25D366] text-white hover:bg-[#20BD5A]"
                    : "bg-white text-[#2D1810] hover:bg-[#F4EDE4]"
                }`}
                data-testid="whatsapp-nav-btn"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">Pedir ahora</span>
              </motion.a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-3 rounded-full transition-colors ${
                  isScrolled
                    ? "text-[#2D1810] hover:bg-[#F4EDE4]"
                    : "text-white hover:bg-white/10"
                }`}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" strokeWidth={1.5} />
                ) : (
                  <Menu className="w-6 h-6" strokeWidth={1.5} />
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <motion.div
        initial={false}
        animate={{
          height: isMobileMenuOpen ? "auto" : 0,
          opacity: isMobileMenuOpen ? 1 : 0,
        }}
        className="fixed top-16 md:top-20 left-0 right-0 z-40 bg-[#FDFBF7] shadow-lg overflow-hidden lg:hidden"
        data-testid="mobile-menu"
      >
        <div className="p-4 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-colors ${
                activeSection === item.id
                  ? "bg-[#C88037] text-white"
                  : "text-[#2D1810] hover:bg-[#F4EDE4]"
              }`}
            >
              {item.label}
            </button>
          ))}
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white py-3 rounded-xl font-semibold mt-4"
          >
            <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
            Pedir por WhatsApp
          </a>
        </div>
      </motion.div>
    </>
  );
};

export default Navbar;
